import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../services/api';

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const DIAS_SEMANA = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

function pad(n) {
  return String(n).padStart(2, '0');
}

function isoDeCelda(anio, mesIndex, dia) {
  return `${anio}-${pad(mesIndex + 1)}-${pad(dia)}`;
}

// Admin: marca/desmarca los días festivos de toda la empresa. Se reflejan en
// color naranja claro en los informes de jornada de todos los trabajadores,
// igual que las vacaciones (que cada trabajador marca por su cuenta).
export default function Festivos() {
  const hoy = new Date();
  const [mesVista, setMesVista] = useState(hoy.getMonth());
  const [anioVista, setAnioVista] = useState(hoy.getFullYear());
  const [festivos, setFestivos] = useState([]); // [{fecha, nombre}]
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(null); // fecha en curso de guardado
  const [msg, setMsg] = useState(null);
  const [nombreNuevo, setNombreNuevo] = useState('');

  // Quién trabajó y quién estuvo de vacaciones cada día del mes en vista
  // (solo nombres, sin horario) — puramente visual, para ver el mes de un vistazo.
  const [asistencia, setAsistencia] = useState({}); // { 'YYYY-MM-DD': { trabajaron: [...], vacaciones: [...] } }
  const [loadingAsistencia, setLoadingAsistencia] = useState(true);

  const fetchFestivos = useCallback(async (anio) => {
    setLoading(true);
    try {
      const res = await api.get(`/festivos?anio=${anio}`);
      setFestivos(res.data.festivos || []);
    } catch {
      setMsg({ tipo: 'err', texto: 'No se pudieron cargar los festivos' });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAsistencia = useCallback(async (anio, mesIndex) => {
    setLoadingAsistencia(true);
    try {
      const res = await api.get(`/admin/asistencia-mes?anio=${anio}&mes=${mesIndex + 1}`);
      setAsistencia(res.data.dias || {});
    } catch {
      // Si falla, el calendario sigue funcionando igual, simplemente sin los nombres.
      setAsistencia({});
    } finally {
      setLoadingAsistencia(false);
    }
  }, []);

  useEffect(() => {
    fetchFestivos(anioVista);
  }, [anioVista, fetchFestivos]);

  useEffect(() => {
    fetchAsistencia(anioVista, mesVista);
  }, [anioVista, mesVista, fetchAsistencia]);

  const festivosPorFecha = useMemo(() => {
    const m = new Map();
    for (const f of festivos) m.set(f.fecha, f.nombre || '');
    return m;
  }, [festivos]);

  const cambiarMes = (delta) => {
    let m = mesVista + delta;
    let a = anioVista;
    if (m < 0) { m = 11; a -= 1; }
    if (m > 11) { m = 0; a += 1; }
    setMesVista(m);
    if (a !== anioVista) setAnioVista(a);
  };

  const toggleDia = async (fecha) => {
    if (guardando) return;
    setGuardando(fecha);
    setMsg(null);
    const marcado = festivosPorFecha.has(fecha);
    try {
      if (marcado) {
        await api.delete(`/festivos/${fecha}`);
        setFestivos((prev) => prev.filter((f) => f.fecha !== fecha));
      } else {
        const nombre = nombreNuevo.trim();
        await api.post('/festivos', { fecha, nombre });
        setFestivos((prev) => [...prev, { fecha, nombre }].sort((a, b) => a.fecha.localeCompare(b.fecha)));
        setNombreNuevo('');
      }
    } catch (err) {
      setMsg({ tipo: 'err', texto: err.response?.data?.error || 'No se pudo actualizar el día' });
    } finally {
      setGuardando(null);
    }
  };

  // Construye la cuadrícula del mes en vista (semanas de lunes a domingo)
  const celdas = useMemo(() => {
    const primerDia = new Date(anioVista, mesVista, 1);
    const diasEnMes = new Date(anioVista, mesVista + 1, 0).getDate();
    const offset = (primerDia.getDay() + 6) % 7; // lunes = 0

    const arr = [];
    for (let i = 0; i < offset; i++) arr.push(null);
    for (let d = 1; d <= diasEnMes; d++) arr.push(d);
    return arr;
  }, [mesVista, anioVista]);

  const hoyIso = `${hoy.getFullYear()}-${pad(hoy.getMonth() + 1)}-${pad(hoy.getDate())}`;
  const festivosDelMesVista = festivos
    .filter((f) => f.fecha.startsWith(`${anioVista}-${pad(mesVista + 1)}`))
    .sort((a, b) => a.fecha.localeCompare(b.fecha));

  return (
    <div className="flex flex-col gap-4">
      <div className="card">
        <h3 className="font-semibold text-gray-700 mb-1">🎉 Días Festivos</h3>
        <p className="text-xs text-gray-500">
          Marca aquí los festivos de la empresa (nacionales, autonómicos o locales). Se reflejarán en color naranja
          claro en los informes de jornada de todos los trabajadores, igual que las vacaciones. Además, cada día
          muestra quién ha trabajado y quién ha estado de vacaciones, solo como referencia visual del mes.
        </p>
      </div>

      <div className="card">
        <label className="label">Nombre del festivo (opcional, se usa al marcar el próximo día)</label>
        <input
          type="text"
          className="input"
          placeholder="p. ej. Día de la Comunidad de Madrid"
          value={nombreNuevo}
          onChange={(e) => setNombreNuevo(e.target.value)}
        />
      </div>

      {msg && (
        <div className={`px-4 py-3 rounded-xl text-sm font-medium
          ${msg.tipo === 'ok' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
          {msg.texto}
        </div>
      )}

      <div className="card">
        {/* Navegación mes/año */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => cambiarMes(-1)}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
          >
            ‹
          </button>
          <div className="font-semibold text-gray-800 capitalize">
            {MESES[mesVista]} {anioVista}
          </div>
          <button
            onClick={() => cambiarMes(1)}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
          >
            ›
          </button>
        </div>

        {/* Días de la semana (S y D resaltados en verde, igual que en el informe) */}
        <div className="grid grid-cols-7 gap-1 mb-1">
          {DIAS_SEMANA.map((d, i) => (
            <div key={d} className={`text-center text-xs font-medium py-1 ${i >= 5 ? 'text-green-600' : 'text-gray-400'}`}>
              {d}
            </div>
          ))}
        </div>

        {/* Cuadrícula */}
        {loading ? (
          <div className="text-center py-8 text-gray-400 text-sm">Cargando...</div>
        ) : (
          <div className="grid grid-cols-7 gap-1">
            {celdas.map((dia, i) => {
              if (dia === null) return <div key={`b${i}`} />;
              const fecha = isoDeCelda(anioVista, mesVista, dia);
              const marcado = festivosPorFecha.has(fecha);
              const nombre = festivosPorFecha.get(fecha);
              const esHoy = fecha === hoyIso;
              const enCurso = guardando === fecha;
              const diaSemana = new Date(anioVista, mesVista, dia).getDay();
              const esFinde = diaSemana === 0 || diaSemana === 6;

              const infoDia = asistencia[fecha] || {};
              const trabajaron = infoDia.trabajaron || [];
              const vacacionesDia = infoDia.vacaciones || [];

              return (
                <button
                  key={fecha}
                  onClick={() => toggleDia(fecha)}
                  disabled={!!guardando}
                  title={marcado ? (nombre || 'Festivo') : undefined}
                  className={`min-h-[62px] rounded-lg text-left p-1.5 flex flex-col gap-0.5 transition-colors relative
                    disabled:cursor-wait
                    ${marcado
                      ? 'bg-orange-300 hover:bg-orange-400'
                      : esFinde
                        ? 'bg-green-100 hover:bg-green-200'
                        : 'bg-gray-50 hover:bg-gray-100'}
                    ${esHoy ? 'ring-2 ring-mgh-blue-900 ring-offset-1' : ''}`}
                >
                  <span className={`text-sm font-medium self-end leading-none ${marcado ? 'text-orange-900' : 'text-gray-700'}`}>
                    {enCurso ? (
                      <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin inline-block" />
                    ) : dia}
                  </span>

                  {!enCurso && !loadingAsistencia && trabajaron.length > 0 && (
                    <span className="text-[9px] leading-tight text-gray-600 break-words">
                      {trabajaron.join(', ')}
                    </span>
                  )}

                  {!enCurso && !loadingAsistencia && vacacionesDia.length > 0 && (
                    <span className="text-[9px] leading-tight text-amber-800 break-words">
                      🏖️ {vacacionesDia.join(', ')}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Leyenda */}
        <div className="flex items-center gap-4 mt-4 pt-3 border-t border-gray-100 text-xs text-gray-500 flex-wrap">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-orange-300 inline-block" />
            Festivo
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-green-100 inline-block" />
            Fin de semana
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-gray-50 border border-gray-200 inline-block" />
            Día normal
          </div>
          <div className="flex items-center gap-1.5 text-gray-600">
            Nombres = trabajaron ese día
          </div>
          <div className="flex items-center gap-1.5 text-amber-800">
            🏖️ = de vacaciones
          </div>
        </div>
      </div>

      {festivosDelMesVista.length > 0 && (
        <div className="card">
          <div className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
            Festivos de {MESES[mesVista].toLowerCase()}
          </div>
          <ul className="space-y-1.5">
            {festivosDelMesVista.map((f) => (
              <li key={f.fecha} className="flex items-center justify-between text-sm">
                <span className="text-gray-700">
                  {f.fecha.split('-').reverse().join('/')}{f.nombre ? ` — ${f.nombre}` : ''}
                </span>
                <button onClick={() => toggleDia(f.fecha)} className="text-xs text-red-600 hover:underline">
                  Quitar
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
